import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogContent } from '@mui/material';

import {
    clearValidationErrors,
    createPlacement,
    loadBusinessRecord
} from '../../../../../store/recruitmentService';
import { getConfiguredItems, getObjFromId } from '../../../../../utils/directusFunctions';
import { formatFloat } from '../../../../../utils/numberUtils';
import { hasRole } from '../../../../../utils/userRoles';
import Button from '../../../../formElements/Button';
import DateSelect from '../../../../formElements/DateSelect';
import RadioButtons from '../../../../formElements/RadioButtons';
import SingleSelect from '../../../../formElements/SingleSelect';
import Switch from '../../../../formElements/Switch';
import TextInputField from '../../../../formElements/TextInputField';
import { validate } from '../validation/validateAddPlacementEditor';

import '../../../../../commonStyles/muiStyles.css';
import form from '../../../../../commonStyles/formStyles.module.css';
import modal from '../../../../../commonStyles/modal.module.css';

const AddPlacementEditor = ({
    roles,
    acceptedRoles,
    isOpen,
    onPlacementEditorToggle,
    onPlacementCreated
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        businessName: '',
        city: '',
        claimableEvent: true,
        employmentTypeId: '',
        fixedFlexibleHour: 'fixed',
        fullServiceDeclined: false,
        hiringManagerEmail: '',
        hiringManagerId: '',
        hiringManagerName: '',
        hiringManagerOfficeNumber: '',
        hiringManagerMobileNumber: '',
        hourlyPay: '',
        hoursPerWeek: '',
        hoursPerWeekId: '',
        jobSectorId: '',
        jobStartEvidence: false,
        participantId: '',
        postcode: '',
        startDate: '',
        vacancyId: '',
        vacancyTitle: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateEntry, setInitialStateEntry] = useState(initialState);
    const [arrayPlacementEmploymentTypes, setArrayPlacementEmploymentTypes] = useState([]);
    const [
        placementEmploymentTypeWithoutSelfEmployed,
        setPlacementEmploymentTypeWithoutSelfEmployed
    ] = useState([]);
    const [errors, setErrors] = useState({});
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const hoursPerWeekDetails = useSelector(
        (state) => state.entities.directusService.hoursPerWeekDetails
    );
    const placementEmploymentTypeDetails = useSelector(
        (state) => state.entities.directusService.placementEmploymentTypeDetails
    );
    const currentBusiness = useSelector(
        (state) => state.entities.recruitmentService.currentBusiness
    );
    const currentVacancy = useSelector((state) => state.entities.recruitmentService.currentVacancy);
    const validationErrors = useSelector(
        (state) => state.entities.recruitmentService.validationErrors
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (placementEmploymentTypeDetails?.length < 1) return;
        setPlacementEmploymentTypeWithoutSelfEmployed(
            placementEmploymentTypeDetails.filter((el) => el.name !== 'Self Employed')
        );
    }, [placementEmploymentTypeDetails]);

    useEffect(() => {
        if (
            placementEmploymentTypeWithoutSelfEmployed?.length < 1 ||
            !currentParticipant?.contractId
        )
            return;
        setArrayPlacementEmploymentTypes(
            getConfiguredItems(
                placementEmploymentTypeWithoutSelfEmployed,
                currentParticipant?.contractId
            )
        );
    }, [placementEmploymentTypeWithoutSelfEmployed, currentParticipant?.contractId]);

    useEffect(() => {
        if (Object.keys(currentVacancy).length < 1) return;
        dispatch(loadBusinessRecord(currentVacancy.businessRecordId));
    }, [currentVacancy]);

    useEffect(() => {
        if (
            Object.keys(currentVacancy).length < 1 ||
            Object.keys(currentBusiness).length < 1 ||
            currentVacancy?.businessRecordId !== currentBusiness?.id
        )
            return;

        const contactDetails = currentBusiness?.contactCards.find(
            (entry) => entry.id === currentVacancy.hiringManagerId
        );
        const existingState = {
            ...newEntry,
            businessName: currentVacancy.businessName,
            hiringManagerId: currentVacancy.hiringManagerId,
            jobSectorId: currentVacancy.sectorId,
            vacancyId: currentVacancy.id,
            vacancyTitle: currentVacancy.title,
            addressLine1: currentBusiness.addressLine1,
            addressLine2: currentBusiness.addressLine2,
            addressLine3: currentBusiness.addressLine3,
            city: currentBusiness.city,
            postcode: currentBusiness.postcode,
            hiringManagerEmail: contactDetails?.email || '',
            hiringManagerName: `${contactDetails?.firstName || ''} ${
                contactDetails?.lastName || ''
            }`,
            hiringManagerOfficeNumber: contactDetails?.officeNumber || '',
            hiringManagerMobileNumber: contactDetails?.mobileNumber || ''
        };
        setNewEntry(existingState);
        setInitialStateEntry(existingState);
    }, [currentBusiness, currentVacancy]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.startsWith(`Job placement has been added`)) {
            clearData();
            onPlacementCreated();
        }
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            dispatch(clearValidationErrors());
        }
    }, [successMessage, validationErrors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    const clearData = () => {
        setNewEntry(initialState);
        setErrors({});
        isUpdateButtonDisabled && setIsUpdateButtonDisabled(false);
        onPlacementEditorToggle(false);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSwitchFixedOrFlexible = (e) => {
        clearError('hoursPerWeek');
        clearError('hoursPerWeekId');
        const { checked } = e.target;
        checked
            ? setNewEntry((prev) => ({
                  ...prev,
                  hoursPerWeek: null,
                  fixedFlexibleHour: 'flexible'
              }))
            : setNewEntry((prev) => ({ ...prev, hoursPerWeekId: '', fixedFlexibleHour: 'fixed' }));
    };

    const onCancel = (e, reason) => {
        if (reason === 'backdropClick') return;
        clearData();
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            participantId: currentParticipant.id
        };
        const msg = `Job placement has been added: ${payload.vacancyTitle}`;
        dispatch(createPlacement(payload, msg));
    };

    // RENDER
    return (
        <Dialog
            className="muiDialogWrapper"
            open={isOpen}
            onClose={onCancel}
            fullWidth
            aria-labelledby="form-dialog-title">
            <div className={modal.modalHeader}>
                <h3>Create Placement</h3>
            </div>
            <div className={modal.modalContent}>
                <form
                    onSubmit={onSubmit}
                    className={form.formWrapper}
                    data-testid="add-placement-edit-form">
                    <DialogContent data-testid="dialog-content">
                        <div className={form.formSection}>
                            <div className={form.formColumn}>
                                <TextInputField
                                    id="hourlyPay"
                                    label="Hourly Pay"
                                    placeholder="Enter hourly pay"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    mandatory={true}
                                    maxLength={6}
                                    value={newEntry.hourlyPay || ''}
                                    error={errors.hourlyPay}
                                    onChange={(e) =>
                                        onUpdate('hourlyPay', formatFloat(e.target.value))
                                    }
                                />
                                <Switch
                                    id="fixedFlexibleHour"
                                    label="Fixed or Flexible"
                                    leftLabel="Fixed"
                                    rightLabel="Flexible"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    checked={newEntry.fixedFlexibleHour === 'flexible'}
                                    onChange={onSwitchFixedOrFlexible}
                                />
                                {newEntry.fixedFlexibleHour === 'fixed' ? (
                                    <TextInputField
                                        id="hoursPerWeek"
                                        label="Hours per Week"
                                        placeholder="Enter hours per week"
                                        disabled={!hasRole(acceptedRoles, roles)}
                                        mandatory={newEntry.fixedFlexibleHour === 'fixed'}
                                        maxLength={5}
                                        value={newEntry.hoursPerWeek || ''}
                                        error={errors.hoursPerWeek}
                                        onChange={(e) =>
                                            onUpdate('hoursPerWeek', formatFloat(e.target.value))
                                        }
                                    />
                                ) : (
                                    <SingleSelect
                                        id="hoursPerWeekId"
                                        label="Hours per Week"
                                        placeholder="Select hours per week..."
                                        disabled={!hasRole(acceptedRoles, roles)}
                                        mandatory={newEntry.fixedFlexibleHour === 'flexible'}
                                        menuItems={hoursPerWeekDetails}
                                        selectedId={newEntry.hoursPerWeekId || ''}
                                        selected={getObjFromId(
                                            hoursPerWeekDetails,
                                            newEntry.hoursPerWeekId
                                        )}
                                        error={errors.hoursPerWeekId}
                                        onChange={(chosenId) =>
                                            onUpdate('hoursPerWeekId', chosenId)
                                        }
                                    />
                                )}
                            </div>
                            <div className={form.formColumn}>
                                <RadioButtons
                                    id="claimableEvent"
                                    label="Claimable Event?"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    value={newEntry.claimableEvent ?? true}
                                    onChange={(option) => onUpdate('claimableEvent', option)}
                                />
                                <SingleSelect
                                    id="employmentTypeId"
                                    label="Placement Employment Type"
                                    placeholder="Select placement employment type..."
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    mandatory={true}
                                    customClass="absoluteAndHeightControl"
                                    menuItems={arrayPlacementEmploymentTypes}
                                    selectedId={newEntry.employmentTypeId || ''}
                                    selected={getObjFromId(
                                        arrayPlacementEmploymentTypes,
                                        newEntry?.employmentTypeId
                                    )}
                                    error={errors?.employmentTypeId}
                                    onChange={(chosenId) => onUpdate('employmentTypeId', chosenId)}
                                />
                                <DateSelect
                                    id="startDate"
                                    label="Start Date"
                                    disabled={!hasRole(acceptedRoles, roles)}
                                    mandatory={true}
                                    value={newEntry.startDate || ''}
                                    error={errors.startDate}
                                    onDateChange={(date) => onUpdate('startDate', date)}
                                />
                            </div>
                        </div>
                    </DialogContent>

                    <div className={modal.actionButtons}>
                        <Button
                            id="addPlacementEditorUpdateButton"
                            disabled={!hasRole(acceptedRoles, roles) || isUpdateButtonDisabled}
                            content="Update"
                            error={errors.button}
                            clearError={() => clearError('button')}
                        />
                        <Button
                            id="addPlacementEditorCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onCancel}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

AddPlacementEditor.propTypes = {
    roles: PropTypes.arrayOf(PropTypes.string),
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    isOpen: PropTypes.bool.isRequired,
    onPlacementEditorToggle: PropTypes.func,
    onPlacementCreated: PropTypes.func.isRequired
};

export default AddPlacementEditor;
